import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";
import isEmpty from "lodash-es/isEmpty";
import throttle from "lodash-es/throttle";

import type { NWHighlight } from "@/components/nw-highlight";
import { Store } from "@/store";

import { getElementsForGroup } from "../../utils/nodes";
import { HighlightInteractionEvent, isHighlightAddedEvent, isHighlightInteractedEvent } from "./events";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    highlightInteraction: {
      /**
       * Activate the highlight interaction for a specific group
       * @param group The ID of the highlight group to activate
       */
      activateHighlightGroup: (group: string) => ReturnType;
      /**
       * Deactivate the highlight interaction for a specific group
       * @param group The ID of the highlight group to deactivate
       */
      deactivateHighlightGroup: (group: string) => ReturnType;
    };
  }
}

export const HighlightInteractionExtension = (() => {
  const throttleTimeMs = 50;

  let lastAddedTime = 0;
  const throttledInteraction = throttle(
    (view: EditorView, group: string) => setGroupActive(view, group),
    throttleTimeMs,
  );

  /**
   * Toggles the active state of highlight groups in the editor view.
   * @param view - The EditorView instance.
   * @param group - The group identifier.
   * @param toActive - Whether to set the group as active or inactive.
   */
  function toggleHighlightGroup(view: EditorView, group: string, toActive: boolean): void {
    const nodes = getElementsForGroup<NWHighlight>(view, group, "nw-highlight");
    nodes.forEach((node: NWHighlight) => {
      node.setActive(toActive);
    });
  }

  /**
   * Sets a highlight group as active in the editor view.
   * @param view - The EditorView instance.
   * @param group - The group identifier to set as active.
   * @returns A boolean indicating whether the operation was successful.
   */
  function setGroupActive(view: EditorView, group: string): boolean {
    const existingGroup: string | undefined = Store.editor.activeHighlightGroup.peek();

    if (isEmpty(group)) {
      return false;
    }

    if (existingGroup !== undefined) {
      toggleHighlightGroup(view, existingGroup, false);
    }

    Store.editor.setActiveHighlightGroup(group);

    toggleHighlightGroup(view, group, true);
    return true;
  }

  return Extension.create({
    name: "highlightInteraction",

    addProseMirrorPlugins() {
      if (!Store.features.hasFeature("projects.highlights")) {
        return [];
      }

      const pluginKey = new PluginKey("highlightInteraction");

      return [
        new Plugin({
          key: pluginKey,
          props: {
            handleDOMEvents: {
              [HighlightInteractionEvent.HIGHLIGHT_INTERACTED]: (view: EditorView, event: Event) => {
                if (!isHighlightInteractedEvent(event)) {
                  return false;
                }

                const { group } = event.detail;
                const currentTime = Date.now();

                if (currentTime - lastAddedTime < throttleTimeMs) {
                  event.preventDefault();
                  return false;
                }

                throttledInteraction(view, group);
                return true;
              },
              [HighlightInteractionEvent.HIGHLIGHT_ADDED]: (view: EditorView, event: Event) => {
                if (!isHighlightAddedEvent(event)) {
                  return false;
                }

                const { group } = event.detail;
                lastAddedTime = Date.now();

                return setGroupActive(view, group);
              },
            },
          },
        }),
      ];
    },

    addCommands() {
      return {
        activateHighlightGroup:
          (group: string) =>
          ({ view }) => {
            const currentTime = Date.now();
            if (currentTime - lastAddedTime < throttleTimeMs) {
              return false;
            }

            return throttledInteraction(view, group);
          },
        deactivateHighlightGroup:
          (group: string) =>
          ({ view }) => {
            toggleHighlightGroup(view, group, false);
            Store.editor.setActiveHighlightGroup(undefined);
            return true;
          },
      };
    },
  });
})();
